import type { ClassDefinition } from '../../schemas/classDefinition';
import { dwrClassesById } from '../../data/classes';
import { findMoveById } from '../../data/moves';
import { findSpellsByClass, findSpellsByClassAndTradition } from '../../data/spells';
import { getActiveSpellLists, isSpellAccessAvailable } from '../../services/spellAccess';
import type { Spell, SpellAccess } from '../../schemas/spell';
import { inferEquipmentSelectionsFromInventory } from './equipmentSelections';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from './services/moveRefMutations';
import { isMoveSetupComplete } from './services/moveSetupComplete';
import {
  type BuilderAction,
  type BuilderSectionId,
  type BuilderState,
  type CharacterDraft,
  createEmptyDraft,
} from './types';

/**
 * Reducer puro del builder. Tutte le mutazioni del draft passano da qui.
 *
 * **Strutturato come dispatch tabellato** per restare snello: le azioni
 * "set field" vivono in una mappa di field-update, mentre i due casi con
 * side-effect (`SET_CLASS` con cleanup, `INIT_DRAFT`/`RESET_DRAFT` di
 * lifecycle) hanno funzioni dedicate.
 */
export function builderReducer(state: BuilderState, action: BuilderAction): BuilderState {
  if (action.type === 'INIT_DRAFT') return { draft: action.draft };
  if (action.type === 'RESET_DRAFT') return { draft: createEmptyDraft() };
  if (action.type === 'SET_CLASS') {
    return { draft: applyClassChange(state.draft, action.classId) };
  }
  return { draft: applyFieldUpdate(state.draft, action) };
}

/**
 * Cambiare classe invalida le scelte specifiche-classe (archetipo, mosse,
 * aspetti, equipaggiamento, allineamento). Mantiene razza, stats, nome,
 * perché ortogonali alla classe.
 *
 * **FI-20 (D9)**: i `bonds` vengono **mantenuti** invece che azzerati. Una
 * volta compilati, i bond sono testo libero (`BondSchema.text` + optional
 * `targetCharacterName`) senza riferimento al template di classe — un bond
 * scritto come Bardo resta valido narrativamente anche se la classe cambia.
 * Truncate se `bonds.length` eccede il `maxBonds` della nuova classe (es.
 * Bardo maxBonds=6 → Mago maxBonds=3 ⇒ truncate ai primi 3). `bondsDeferred`
 * è preservato (decisione PG-level «compilo più tardi», ortogonale alla
 * classe).
 *
 * **FI-29**: anche `alignment` viene resettato. La categoria di allineamento
 * (Buono/Legale/Neutrale/Caotico/Malvagio) è universale, ma il `precept` è
 * class-specific (es. Bardo Buono = «Usi la tua arte per aiutare qualcuno»;
 * Paladino Buono = «Sfidi il pericolo per proteggere chi è più debole»). Se
 * non resettiamo, il precept resta agganciato al testo della classe
 * precedente, incoerente con la nuova classe. Il giocatore deve riselezionare
 * l'allineamento (con il precept della nuova classe).
 */
function applyClassChange(
  draft: CharacterDraft,
  classId: CharacterDraft['classId'],
): CharacterDraft {
  const newMaxBonds = classId !== undefined ? dwrClassesById[classId]?.maxBonds : undefined;
  const truncatedBonds =
    newMaxBonds !== undefined && draft.bonds.length > newMaxBonds
      ? draft.bonds.slice(0, newMaxBonds)
      : draft.bonds;
  return {
    ...draft,
    classId,
    archetypeId: undefined,
    alignment: undefined,
    chosenAspetti: [],
    moves: [],
    spells: [],
    inventory: [],
    bonds: truncatedBonds,
  };
}

/**
 * Aggiorna un singolo campo del draft. Splittato in 3 sotto-funzioni per
 * mantenere ognuna entro i limiti di complessità (un solo switch da 11
 * case sforerebbe il limite di 10).
 */
function applyFieldUpdate(draft: CharacterDraft, action: BuilderAction): CharacterDraft {
  return (
    applyIdentityField(draft, action) ??
    applyMechanicalField(draft, action) ??
    applyContentField(draft, action) ??
    applyMoveSetupField(draft, action) ??
    draft
  );
}

/** name, title, notes. */
function applyIdentityField(draft: CharacterDraft, action: BuilderAction): CharacterDraft | null {
  switch (action.type) {
    case 'SET_NAME':
      return { ...draft, name: action.name };
    case 'SET_TITLE':
      return { ...draft, title: action.title };
    case 'SET_NOTES':
      return { ...draft, notes: action.notes };
    default:
      return null;
  }
}

/** race, archetypeId, stats, alignment. */
function applyMechanicalField(draft: CharacterDraft, action: BuilderAction): CharacterDraft | null {
  switch (action.type) {
    case 'SET_RACE':
      return { ...draft, race: action.race };
    case 'SET_ARCHETYPE':
      return { ...draft, archetypeId: action.archetypeId };
    case 'SET_STATS':
      return { ...draft, stats: action.stats };
    case 'CLEAR_STATS':
      // **FI-69**: usato da `SectionStats` quando l'utente fa switch e una
      // stat torna nel pool. Senza questo dispatch il context conserverebbe
      // un Stats completo precedente e il predicate `isSectionComplete('stats')`
      // rimarrebbe `true` (sezione marcata "completata" con UI in stato
      // parziale → finalize gate disallineato).
      return { ...draft, stats: undefined };
    case 'SET_ALIGNMENT':
      return { ...draft, alignment: action.alignment };
    default:
      return null;
  }
}

/** chosenAspetti, inventory, moves, spells, bonds. */
function applyContentField(draft: CharacterDraft, action: BuilderAction): CharacterDraft | null {
  switch (action.type) {
    case 'SET_ASPETTI':
      return { ...draft, chosenAspetti: action.chosenAspetti };
    case 'SET_INVENTORY':
      return { ...draft, inventory: action.inventory };
    case 'SET_MOVES':
      return { ...draft, moves: action.moves };
    case 'SET_SPELLS':
      return { ...draft, spells: action.spells };
    case 'SET_BONDS':
      return { ...draft, bonds: action.bonds };
    case 'MARK_BONDS_DEFERRED':
      return { ...draft, bondsDeferred: action.bondsDeferred };
    default:
      return null;
  }
}

/**
 * **FI-18 Fase 2** — setup mossa: `selectedChoiceGroups` (multi-gruppo) e
 * `narrativeFieldValues` (campi narrativi). Aggiornano in-place il
 * `MoveRef` corrispondente in `draft.moves`. Se la mossa non è in `moves`,
 * l'azione è no-op (il MoveRef viene creato da `SET_MOVES` quando la mossa
 * entra nel set selezionato — `SectionStartingMoves`).
 *
 * **Cluster C1 Audit 2 (2026-05-16)**: la mutazione del singolo `MoveRef` è
 * delegata ai helper SSOT in `./services/moveRefMutations`, che gestiscono
 * correttamente il branch shorthand `groupId === 'main'` (scrittura in
 * `selectedChoices` invece di `selectedChoiceGroups['main']`). Prima del
 * refactor il reducer aveva 2 funzioni private che mancavano del branch
 * shorthand — bug latente se la UI dispatchasse con `groupId='main'`.
 */
function applyMoveSetupField(draft: CharacterDraft, action: BuilderAction): CharacterDraft | null {
  switch (action.type) {
    case 'SET_MOVE_CHOICE_GROUP':
      return {
        ...draft,
        moves: draft.moves.map((m) =>
          updateMoveChoiceGroup(m, action.moveId, action.groupId, action.selectedIds),
        ),
      };
    case 'SET_MOVE_NARRATIVE_FIELD':
      return {
        ...draft,
        moves: draft.moves.map((m) =>
          updateMoveNarrativeField(m, action.moveId, action.fieldId, action.value),
        ),
      };
    default:
      return null;
  }
}

/**
 * Predicato di completezza per una sezione. SSOT sul `draft` — nessuno
 * stato di "sezione completata" è memorizzato altrove.
 *
 * Implementato come dispatch tabellato per restare entro i limiti di
 * complessità.
 */
export function isSectionComplete(
  section: BuilderSectionId,
  draft: CharacterDraft,
  classDef: ClassDefinition | undefined,
): boolean {
  const predicate = SECTION_COMPLETE_PREDICATES[section];
  return predicate(draft, classDef);
}

type SectionPredicate = (draft: CharacterDraft, classDef: ClassDefinition | undefined) => boolean;

const SECTION_COMPLETE_PREDICATES: Record<BuilderSectionId, SectionPredicate> = {
  class: (draft) => draft.classId !== undefined,
  race: (draft) => draft.race !== undefined,
  archetype: (draft) => draft.archetypeId !== undefined,
  stats: (draft) => draft.stats !== undefined,
  alignment: (draft) => (draft.alignment?.precept.length ?? 0) > 0,
  aspetti: (draft, classDef) => draft.chosenAspetti.length === (classDef?.aspetti.length ?? -1),
  equipment: (draft, classDef) => {
    if (!classDef) return false;
    // Verifica che gli item `always` siano presenti in inventory.
    if (draft.inventory.length < classDef.startingEquipment.always.length) return false;
    // **FI-28**: verifica che ogni gruppo di scelta abbia raggiunto il
    // proprio `pickCount`. Le selezioni sono inferite da `inventory` via
    // `inferEquipmentSelectionsFromInventory` (riusato da `SectionEquipment`).
    const selections = inferEquipmentSelectionsFromInventory(
      draft.inventory,
      classDef.startingEquipment.choices,
    );
    for (const group of classDef.startingEquipment.choices) {
      const matched = selections[group.id] ?? [];
      if (matched.length < group.pickCount) return false;
    }
    // **FI-50 (2026-05-22)**: descrizioni narrative obbligatorie compilate.
    return areAllUserDescriptionsFilled(draft.inventory);
  },
  'starting-moves': (draft, classDef) => {
    if (!classDef) return false;
    // expected = mosse iniziali auto + mossa concessa dall'archetipo (se
    // selezionato) + somma dei pickCount di ogni `StartingMoveChoiceGroup`.
    // La mossa dell'archetipo è inclusa solo se l'archetipo è già scelto;
    // altrimenti la sezione `archetype` blocca comunque la finalize.
    const expected =
      classDef.startingMoves.length +
      (draft.archetypeId ? 1 : 0) +
      classDef.startingChoiceGroups.reduce((s, g) => s + g.pickCount, 0);
    if (draft.moves.length < expected) return false;
    // **FI-18 Fase 2 + FI-32**: per ogni mossa selezionata il setup
    // strutturato deve essere completo. `isMoveSetupComplete` unifica i 3
    // pattern (choices shorthand, choiceGroups multi-gruppo, narrativeFields
    // required) e considera le opzioni `freeForArchetype` come bonus non
    // conteggiate nel `pickCount`. Mosse senza setup ritornano sempre true.
    return draft.moves.every((moveRef) => {
      const move = findMoveById(classDef.id, moveRef.id);
      if (!move) return true;
      return isMoveSetupComplete(move, moveRef, draft.archetypeId);
    });
  },
  spells: (draft) => isSpellsSectionComplete(draft),
  'identity-bonds': (draft) =>
    (draft.name?.length ?? 0) > 0 && (draft.bonds.length >= 1 || draft.bondsDeferred === true),
};

/**
 * **FI-50 (2026-05-22)** — Verifica che ogni item nell'inventory con un
 * `userDescriptionPrompt` abbia `userDescription` non vuoto (post trim).
 * La descrizione narrativa fa parte dell'oggetto: senza, il PG ha un
 * placeholder semanticamente vuoto. Estratto dal predicate `equipment`
 * per restare entro la soglia di complessità ciclomatica (10).
 */
function areAllUserDescriptionsFilled(inventory: CharacterDraft['inventory']): boolean {
  for (const item of inventory) {
    if (item.userDescriptionPrompt && (item.userDescription?.trim().length ?? 0) === 0) {
      return false;
    }
  }
  return true;
}

/**
 * Predicate dedicato per la sezione `'spells'`. SSOT della regola di
 * completezza per ogni spell-list attiva del PG.
 *
 * **Mode supportati**:
 * - `'mago-native'`: 3 trucchetti auto + 3 spell L1 = 6 spell totali
 * - `'chierico-native'`: TUTTI gli spell L0+L1 della Tradizione (auto, no
 *   pick); il numero atteso è derivato da `findSpellsByClassAndTradition`,
 *   tenendo conto del filtro `requiresArchetype` per *Voce della Terra*
 * - `'druido-native'`: TUTTI gli spell L0+L1 della Tradizione della Natura
 *   (auto, no pick), Tradizione implicita unica
 * - `'paladino-native'`: TUTTI gli spell L0+L1 della Tradizione (auto, no
 *   pick); analogo a chierico-native ma con 3 Tradizioni (Negromanzia/
 *   Valore/Natura). Tradizione Natura richiede archetipo Elfo (FI-2)
 * - `'warlock-native'` (D2 PR Warlock): TUTTE le Suppliche L0 (auto) +
 *   scegli 2 di N spell L1. Pattern simile a `mago-native` (cantrip auto +
 *   pick K). Trigger: `warlock.vincolo-del-tomo` o
 *   `paladino.ordine-sovrannaturale + ['vincolo-del-tomo']` (FI-27 chiuso).
 *
 * **PG senza spell-list attive** (es. Bardo L1 senza multiclasse, Warlock con
 * Vincolo della Catena/Lama, Paladino-Sovrannaturale + Catena/Lama o senza
 * Vincolo configurato): la sezione mostra il placeholder "Non hai accesso a
 * incantesimi" e il predicate ritorna `true` — non blocca il finalize.
 */
function isSpellsSectionComplete(draft: CharacterDraft): boolean {
  const lists = getActiveSpellLists(draft.moves);
  if (lists.length === 0) return true; // placeholder: sezione non bloccante
  return lists.every((list) => isSpellListComplete(list, draft));
}

/**
 * **FI-45 C4 (2026-05-22)** — predicate dedicato alla completezza di una
 * spell-list attiva del PG. Refactor: l'if-chain a 5 branch è stato
 * suddiviso in 3 famiglie semantiche:
 *  - **mago-native**: pick-only (count >= 6, tutto noto staticamente)
 *  - **warlock-native**: cantrips auto + pick K (count >= N L0 + 2)
 *  - **chierico/druido/paladino-native**: auto-add tutti L0+L1
 *    disponibili al PG → delega a {@link computeAutoNativeExpectation}.
 *    `paladino-native` ha un ulteriore caso degenere "tradizione esiste
 *    ma archetipo incompatibile per tutti gli spell" → setup invalido.
 */
function isSpellListComplete(
  list: ReturnType<typeof getActiveSpellLists>[number],
  draft: CharacterDraft,
): boolean {
  const count = countSpellsForList(list, draft);
  if (list.mode === 'mago-native') {
    // Mago L1: 3 trucchetti auto + 3 spell L1 scelti = 6 spell totali
    return count >= 6;
  }
  if (list.mode === 'warlock-native') {
    // Warlock L1 (D2 PR Warlock): TUTTE le Suppliche L0 auto-aggiunte +
    // scegli 2 spell L1. Pattern simile a Mago: cantrip auto + pick K.
    const cantripsCount = findSpellsByClass('warlock').filter((s) => s.level === 0).length;
    return count >= cantripsCount + 2;
  }
  // chierico/druido/paladino-native: auto-add tutti L0+L1 disponibili.
  const { expectedCount, degenere } = computeAutoNativeExpectation(list, draft);
  if (degenere) return false;
  return count >= expectedCount;
}

/**
 * **FI-45 C4** — SSOT del calcolo "quanti spell L0+L1 della Tradizione
 * sono ATTUALMENTE disponibili al PG" per le 3 spell-list auto-add
 * (chierico, druido, paladino). Centralizza il pattern duplicato 3 volte
 * pre-refactor (filtro `availableTo` per classId+tradizione + filtro
 * `isSpellAccessAvailable` per `requiresArchetype` FI-2 + `requiresMove`
 * FI-24).
 *
 * Il flag `degenere` è valorizzato `true` SOLO per `paladino-native` quando
 * la Tradizione esiste nel manuale (≥1 spell totale) ma il PG non ha
 * l'archetipo richiesto per nessuno (es. Tradizione Natura senza Elfo).
 * In quel caso il predicate ritorna `false` per bloccare il finalize
 * di un setup invalido importato da JSON.
 *
 * Per `chierico-native` e `druido-native` il flag resta sempre `false`
 * (il filtro non degenera a 0 in scenari realistici: gli spell base
 * della Tradizione non hanno `requiresArchetype`).
 */
function computeAutoNativeExpectation(
  list: ReturnType<typeof getActiveSpellLists>[number],
  draft: CharacterDraft,
): { expectedCount: number; degenere: boolean } {
  const ownedMoveIds = draft.moves.map((m) => m.id);
  const allInScope = getAutoNativeSpellsInScope(list).filter((s) => s.level === 0 || s.level === 1);
  const available = allInScope.filter((s) => {
    const access = findAccessForList(s, list);
    if (!access) return true;
    return isSpellAccessAvailable(access, { archetypeId: draft.archetypeId, ownedMoveIds });
  });
  const degenere =
    list.mode === 'paladino-native' && allInScope.length > 0 && available.length === 0;
  return { expectedCount: available.length, degenere };
}

/**
 * **FI-45 C4** — pool base degli spell L0+L1 di una lista auto-native,
 * PRE filtro accessibility. Tradizione-aware per chierico/paladino;
 * implicito per druido (1 sola tradizione `natura`).
 */
function getAutoNativeSpellsInScope(
  list: ReturnType<typeof getActiveSpellLists>[number],
): readonly Spell[] {
  if (list.mode === 'chierico-native') {
    return findSpellsByClassAndTradition('chierico', list.tradizione);
  }
  if (list.mode === 'druido-native') {
    return findSpellsByClass('druido');
  }
  if (list.mode === 'paladino-native') {
    return findSpellsByClassAndTradition('paladino', list.tradizione);
  }
  return [];
}

/**
 * **FI-45 C4** — lookup del `SpellAccess` che fa match con la list per uno
 * spell. Necessario per applicare `isSpellAccessAvailable` (filtro
 * `requiresArchetype` FI-2 + `requiresMove` FI-24).
 *
 * Lo spell deve avere un access che combaci con `classId` + `tradizione`
 * (per chierico/paladino) o solo `classId` (per druido).
 */
function findAccessForList(
  spell: Spell,
  list: ReturnType<typeof getActiveSpellLists>[number],
): SpellAccess | undefined {
  if (list.mode === 'chierico-native') {
    return spell.availableTo.find(
      (a) =>
        a.classId === 'chierico' && (a as { tradizione: string }).tradizione === list.tradizione,
    );
  }
  if (list.mode === 'druido-native') {
    return spell.availableTo.find((a) => a.classId === 'druido');
  }
  if (list.mode === 'paladino-native') {
    return spell.availableTo.find(
      (a) =>
        a.classId === 'paladino' && (a as { tradizione: string }).tradizione === list.tradizione,
    );
  }
  return undefined;
}

function countSpellsForList(
  list: ReturnType<typeof getActiveSpellLists>[number],
  draft: CharacterDraft,
): number {
  // Approssimazione MVP: contiamo l'intero `draft.spells`. Con liste multiple
  // sarà necessario filtrare per `availableTo` matching la list, ma per il
  // L1 di una sola lista alla volta basta `length`.
  // (Quando arriverà il caso multiclasse Chierico+Mago = 2 liste, tornerà
  // qui con un filtro che intersechi `draft.spells` con
  // `findSpellsByClass[AndTradition](list.classId[, list.tradizione])`.)
  void list;
  return draft.spells.length;
}

const ALL_SECTIONS: readonly BuilderSectionId[] = [
  'class',
  'race',
  'archetype',
  'stats',
  'alignment',
  'aspetti',
  'equipment',
  'starting-moves',
  'spells',
  'identity-bonds',
];

/**
 * Tutte le sezioni complete? Esposto a `useFinalizeBuilder` per abilitare il
 * bottone "Finalizza" sia nel `<StepNav>` top (ultima sezione) sia nelle
 * eventuali azioni di topbar.
 */
export function areAllSectionsComplete(
  draft: CharacterDraft,
  classDef: ClassDefinition | undefined,
): boolean {
  if (!classDef) return false;
  return ALL_SECTIONS.every((s) => isSectionComplete(s, draft, classDef));
}
